import { grey } from "@mui/material/colors";

import { pageStyles } from "components/Styled/Page/styles";
import { tableStyles } from "components/Styled/Table/sharedStyles";
import { TemplateContentType } from "features/Influencer/ContactList/schema";

const BADGE_COLORS: Partial<
  Record<TemplateContentType, { bg: string; border: string; text: string }>
> = {
  [TemplateContentType.OUTREACH]: {
    bg: "#f0fdf4",
    border: "#b9f8cf",
    text: "#008236",
  },
  [TemplateContentType.FOLLOWUP]: {
    bg: "#faf5ff",
    border: "#e9d4ff",
    text: "#9810fa",
  },
};

const CARD_BORDER = "rgba(0,0,0,0.1)";
const CARD_SHADOW = "0px 1px 1.5px rgba(0,0,0,0.04)";

const styles = {
  ...pageStyles,
  ...tableStyles,
  root: {
    pb: "60px",
  },

  // Header
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 2,
    pt: 3,
    pb: 2,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: "#0a0a0a",
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: 14,
    color: "#717182",
    mt: 0.5,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexShrink: 0,
  },
  aiAssistantButton: {
    textTransform: "none",
    backgroundColor: "white",
    color: "#0a0a0a",
    border: `1px solid ${CARD_BORDER}`,
    fontWeight: 500,
    fontSize: 14,
    px: 2,
    py: 0.75,
    borderRadius: 2,
    boxShadow: "none",
    "&:hover": { backgroundColor: "#f7f7f5", boxShadow: "none" },
  },
  createTemplateButton: {
    textTransform: "none",
    backgroundColor: "#030213",
    color: "white",
    fontWeight: 500,
    fontSize: 14,
    px: 2,
    py: 0.75,
    borderRadius: 2,
    boxShadow: "none",
    "&:hover": { backgroundColor: "#1a1a2e", boxShadow: "none" },
  },

  // Stats bar
  statsBar: (count: number) => ({
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
      md: "repeat(3, 1fr)",
      lg: `repeat(${count}, 1fr)`,
    },
    gap: 2,
    mb: 2,
  }),
  statCard: (highlight: boolean) => ({
    display: "flex",
    gap: 1.5,
    alignItems: "flex-start",
    p: 2,
    borderRadius: "14px",
    border: `1px solid ${highlight ? "#fee685" : CARD_BORDER}`,
    backgroundColor: highlight ? "#fffbeb" : "white",
    boxShadow: CARD_SHADOW,
  }),
  statCardClickable: {
    cursor: "pointer",
    transition: "box-shadow 0.15s ease",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)" },
  },
  statIcon: (bg: string) => ({
    width: 32,
    height: 32,
    borderRadius: "10px",
    backgroundColor: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  statLabelTop: (highlight: boolean) => ({
    fontSize: 14,
    color: highlight ? "#973c00" : "#717182",
    letterSpacing: "-0.15px",
  }),
  statValue: (color: string) => ({
    fontSize: 20,
    fontWeight: 600,
    color,
    lineHeight: "25px",
    letterSpacing: "-0.45px",
  }),
  statCaption: (highlight: boolean) => ({
    fontSize: 12,
    color: highlight ? "#bb4d00" : "#717182",
    fontWeight: highlight ? 500 : 400,
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    mt: 0.25,
  }),

  // Request a template bar
  requestBar: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 2,
    mb: 3,
    borderRadius: "14px",
    border: `1px solid ${CARD_BORDER}`,
    backgroundColor: "white",
    boxShadow: CARD_SHADOW,
  },
  requestIcon: {
    width: 32,
    height: 32,
    borderRadius: "10px",
    backgroundColor: "#ececf0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#717182",
  },
  requestTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0a0a0a",
  },
  requestBody: {
    fontSize: 13,
    color: "#717182",
  },
  requestButton: {
    textTransform: "none",
    backgroundColor: "white",
    color: "#0a0a0a",
    border: `1px solid ${CARD_BORDER}`,
    fontWeight: 500,
    fontSize: 13,
    px: 1.5,
    py: 0.5,
    borderRadius: 2,
    boxShadow: "none",
    flexShrink: 0,
    "&:hover": { backgroundColor: "#f7f7f5", boxShadow: "none" },
  },

  // Segmented content tabs
  tabsContainer: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    p: 0.5,
    borderRadius: 2,
    backgroundColor: "#ececf0",
  },
  tab: (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.5,
    py: 0.75,
    borderRadius: 1.5,
    cursor: "pointer",
    backgroundColor: active ? "white" : "transparent",
    boxShadow: active ? CARD_SHADOW : "none",
    "&:hover": { backgroundColor: active ? "white" : "rgba(255,255,255,0.5)" },
  }),
  tabLabel: (active: boolean) => ({
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    color: active ? "#0a0a0a" : "#717182",
  }),
  tabCount: (active: boolean) => ({
    fontSize: 12,
    color: active ? "#717182" : "#9a9aa8",
  }),
  controlsRow: {
    mt: 1,
    mb: 2,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 1.5,
  },

  // Card grid
  cardGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, 1fr)",
      lg: "repeat(3, 1fr)",
    },
    gap: 2,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    p: 2,
    borderRadius: "14px",
    border: `1px solid ${CARD_BORDER}`,
    backgroundColor: "white",
    boxShadow: CARD_SHADOW,
    cursor: "pointer",
    transition: "box-shadow 0.15s ease",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)" },
  },
  createCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    height: "100%",
    minHeight: 269,
    p: 2,
    borderRadius: "14px",
    border: `1.5px dashed ${grey[300]}`,
    backgroundColor: "transparent",
    cursor: "pointer",
    color: "#717182",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "#030213",
      color: "#030213",
      backgroundColor: "#fafafa",
    },
  },

  // Card content
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 1.5,
  },
  cardBadge: (contentType: TemplateContentType) => {
    const c = BADGE_COLORS[contentType] ?? {
      bg: "#f0fdf4",
      border: "#b9f8cf",
      text: "#008236",
    };
    return {
      backgroundColor: c.bg,
      border: `1px solid ${c.border}`,
      color: c.text,
      fontSize: 12,
      fontWeight: 500,
      px: 1,
      py: 0.25,
      borderRadius: "8px",
      lineHeight: "16px",
    };
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0a0a0a",
    letterSpacing: "-0.44px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardSubject: {
    fontSize: 14,
    color: "#717182",
    letterSpacing: "-0.15px",
    mt: 0.5,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "& *": { display: "inline" },
  },
  cardBody: {
    fontSize: 14,
    color: "#717182",
    lineHeight: "22.75px",
    letterSpacing: "-0.15px",
    mt: 1,
    mb: 2,
    flex: 1,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    "& *": { display: "inline", margin: 0 },
  },
  cardDivider: {
    height: "1px",
    backgroundColor: "rgba(0,0,0,0.1)",
    mb: 1.5,
  },
  cardStatsRow: {
    display: "flex",
    gap: 2,
    mb: 1.5,
  },
  cardStat: {
    display: "flex",
    flexDirection: "column",
  },
  cardStatValue: (highlight: boolean) => ({
    fontSize: 14,
    fontWeight: 600,
    color: highlight ? "#00a63e" : "#0a0a0a",
    letterSpacing: "-0.15px",
    lineHeight: "20px",
  }),
  cardStatLabel: {
    fontSize: 12,
    color: "#717182",
    lineHeight: "16px",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mt: "auto",
  },
  cardModified: {
    fontSize: 12,
    color: "#717182",
  },
  cardMenuButton: {
    p: 0.5,
    color: grey[500],
    "&:hover": { color: "black" },
  },
  statUpgrade: {
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    color: "purple.main",
  },

  // Folder chips (kept for FolderChips.tsx)
  controlsDivider: {
    width: "1px",
    height: 24,
    mx: 0.5,
    backgroundColor: grey[300],
    display: { xs: "none", md: "block" },
  },
  folderRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 0.75,
  },
  folderChip: (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 2,
    py: 0.75,
    borderRadius: 30,
    cursor: "pointer",
    border: "1px solid",
    borderColor: active ? "primary.main" : "transparent",
    backgroundColor: active ? "primary.main" : "#f7f7f5",
    color: active ? "common.white" : "text.primary",
    fontWeight: active ? 600 : 500,
    boxShadow: active ? "0 1px 6px rgba(0,0,0,0.18)" : "none",
    "&:hover": {
      backgroundColor: active ? "primary.dark" : "#e8e8e4",
    },
  }),
  folderChipLabel: {
    fontSize: 14,
    fontWeight: "inherit",
    color: "inherit",
  },
  folderChipCount: {
    fontSize: 12,
    color: "inherit",
    opacity: 0.65,
  },
  newFolderChip: {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 2,
    py: 0.75,
    borderRadius: 30,
    cursor: "pointer",
    border: "1px dashed",
    borderColor: grey[400],
    color: grey[700],
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "inherit",
    lineHeight: 1.5,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: "black",
      color: "black",
    },
  },
};

export default styles;
